import { useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { useGetOne, useGetResourceLabel, useTranslate } from 'ra-core';
import { useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from '../../components/admin/data-table';
import { DateField } from '../../components/admin/date-field';
import { Error as ErrorPage } from '../../components/admin/error';
import { Loading } from '../../components/admin/loading';
import { AutocompleteArrayInput } from '../../components/admin/autocomplete-array-input';
import { ReferenceArrayInput } from '../../components/admin/reference-array-input';
import { SelectInput } from '../../components/admin/select-input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import { StatusBadge, type StatusLevel } from '../Moonstar/StatusBadge';
import { ListExportButton } from '../../components/custom/ListExportButton';
import { saveSpreadsheet } from '../../utils/spreadsheet/save';
import { useCurrentUser } from '../../auth/currentUserContext';
import { useRestrictedUserUnits } from '../User/useRestrictedUserUnits';
import {
  TaskResponsesDialog,
  type ChecklistTaskCompletion,
} from './TaskResponsesDialog';
import {
  ChecklistDateRangeFilter,
  DEFAULT_CHECKLIST_DATE_RANGE_OPTION,
  getPresetRangeFilter,
} from './ChecklistsListFilters';
import { getChecklistDetailsUrlFilters } from './checklistNavigation';

export function ChecklistDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const translate = useTranslate();
  const getResourceLabel = useGetResourceLabel();
  const { units } = useCurrentUser();
  const [selectedResponse, setSelectedResponse] =
    useState<ChecklistReportEntry | null>(null);
  const {
    isUnitAdmin,
    restrictedUnitIds,
    isPending: areUnitsPending,
    isError: areUnitsError,
    error: unitsError,
    refetch: refetchUnits,
  } = useRestrictedUserUnits();

  const checklist = useGetOne<{ id: string; title: string }>('checklists', {
    id,
  });
  const { period: selectedPeriod, unitId: selectedUnitId } =
    getChecklistDetailsUrlFilters(searchParams);

  const defaultFilters = useMemo(() => {
    const unitIds = isUnitAdmin
      ? selectedUnitId !== undefined &&
        restrictedUnitIds.includes(selectedUnitId)
        ? [selectedUnitId]
        : restrictedUnitIds
      : selectedUnitId !== undefined
        ? [selectedUnitId]
        : undefined;

    return {
      ...(selectedPeriod ??
        getPresetRangeFilter(DEFAULT_CHECKLIST_DATE_RANGE_OPTION)),
      ...(unitIds ? { unitId: unitIds } : {}),
    };
  }, [isUnitAdmin, restrictedUnitIds, selectedPeriod, selectedUnitId]);

  if (checklist.isPending) {
    return <Loading />;
  }

  if (checklist.isError) {
    return (
      <ErrorPage
        resetErrorBoundary={() => void checklist.refetch()}
        error={checklist.error}
      />
    );
  }

  if (isUnitAdmin) {
    if (areUnitsPending) return <Loading />;
    if (areUnitsError) {
      return (
        <ErrorPage
          resetErrorBoundary={() => void refetchUnits()}
          error={unitsError}
        />
      );
    }
  }

  const checklistBreadcrumb =
    units.length === 1
      ? checklist.data.title
      : { label: checklist.data.title, href: `/checklists/${id}` };

  const filters = [
    <ChecklistDateRangeFilter
      key="date-range"
      label="moonstar.checklists.filters.dateRange"
      alwaysOn
    />,
    <ReferenceArrayInput
      key="units"
      label="resources.checklists.fields.unitId"
      source="unitId"
      reference="units"
      filter={isUnitAdmin ? { id: restrictedUnitIds } : undefined}
      perPage={999}
      sort={{ field: 'name', order: 'ASC' }}
      alwaysOn
    >
      <AutocompleteArrayInput
        optionText="name"
        filterToQuery={(query: string) => ({ name: query })}
        controlClassName="min-h-9 py-1"
      />
    </ReferenceArrayInput>,
    <SelectInput
      key="task-status"
      source="taskStatus"
      label="resources.checklists.fields.taskStatus"
      choices={TASK_STATUS_CHOICES}
      alwaysOn
    />,
  ];

  return (
    <MoonstarList<ChecklistReportEntry>
      filters={filters}
      resource={`checklists/${id}/report`}
      exporter={listExporter}
      sort={{ field: 'checklistInstanceStartOn', order: 'DESC' }}
      title={[
        { label: getResourceLabel('checklists', 2), href: '/checklists' },
        checklistBreadcrumb,
        translate('moonstar.checklists.details.title'),
      ]}
      actions={
        <MoonstarListActions hideCreate>
          <ListExportButton maxResults={1_000} />
        </MoonstarListActions>
      }
      filterDefaultValues={defaultFilters}
    >
      <DataTable
        empty={<NoTasksFound />}
        bulkActionButtons={false}
        rowClick={false}
      >
        <DataTable.Col<ChecklistReportEntry>
          source="checklistSnapshotTitle"
          label="resources.checklists.fields.checklistSnapshotTitle"
          disableSort
        />
        <DataTable.Col<ChecklistReportEntry>
          source="unitSnapshotName"
          label="resources.checklists.fields.unitSnapshotName"
          disableSort
        />
        <DataTable.Col<ChecklistReportEntry>
          source="taskSnapshotTitle"
          label="resources.checklists.fields.taskSnapshotTitle"
          className="max-w-[300px]"
          render={(record) => (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      className="block max-w-[300px] truncate rounded-sm text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    />
                  }
                >
                  {record.taskSnapshotTitle}
                </TooltipTrigger>
                <TooltipContent>{record.taskSnapshotTitle}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        />
        <DataTable.Col<ChecklistReportEntry>
          label="resources.checklists.fields.taskStatus"
          disableSort
          render={(record) => <TaskStatusBadge status={record.taskStatus} />}
        />
        <DataTable.Col<ChecklistReportEntry>
          source="checklistInstanceStartOn"
          label="resources.checklists.fields.checklistInstanceStartOn"
        >
          <DateField source="checklistInstanceStartOn" showTime />
        </DataTable.Col>
        <DataTable.Col<ChecklistReportEntry>
          source="taskCompletedOn"
          label="resources.checklists.fields.taskCompletedOn"
        >
          <DateField source="taskCompletedOn" empty="—" showTime />
        </DataTable.Col>
        <DataTable.Col<ChecklistReportEntry>
          source="taskCompletedByFirstName"
          label="resources.checklists.fields.taskCompletedByFirstName"
          render={(record) => record.taskCompletedByFirstName || '—'}
        />
        <DataTable.Col<ChecklistReportEntry>
          source="taskCompletedByLastName"
          label="resources.checklists.fields.taskCompletedByLastName"
          render={(record) => record.taskCompletedByLastName || '—'}
        />
        <DataTable.Col<ChecklistReportEntry>
          label="resources.checklists.fields.responses"
          headerClassName="w-16"
          cellClassName="w-16"
          render={(record) => {
            const isCompleted =
              record.taskStatus === 'COMPLETED' ||
              record.taskStatus === 'COMPLETED_LATE';
            const completion = record.taskCompletionDetails?.completion;
            const hasComment = Boolean(
              completion &&
                completion.type !== 'OPEN_ENDED' &&
                completion.actions?.trim()
            );
            const hasResponse =
              completion?.type === 'OPEN_ENDED'
                ? Boolean(completion.response?.trim())
                : completion?.type === 'RADIO_SELECT'
                  ? completion.selectedOptions.length > 0
                  : completion?.type === 'PHOTO_UPLOAD'
                    ? completion.imageUrls.length > 0
                    : false;

            return isCompleted && (hasResponse || hasComment) ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={translate('moonstar.checklists.responses.view')}
                onClick={() => setSelectedResponse(record)}
              >
                <ClipboardList />
              </Button>
            ) : (
              '—'
            );
          }}
        />
      </DataTable>
      <TaskResponsesDialog
        key={selectedResponse?.id}
        open={selectedResponse !== null}
        record={selectedResponse}
        onClose={() => setSelectedResponse(null)}
      />
    </MoonstarList>
  );
}

function NoTasksFound() {
  const translate = useTranslate();
  return (
    <Alert>
      <AlertDescription>
        {translate('moonstar.checklists.details.noTasks')}
      </AlertDescription>
    </Alert>
  );
}

const taskStatusLevels: Record<
  ChecklistReportEntry['taskStatus'],
  StatusLevel
> = {
  COMPLETED: 'success',
  PENDING: 'warning',
  OVERDUE: 'danger',
  NOT_COMPLETED: 'neutral',
  COMPLETED_LATE: 'neutral',
};

const TASK_STATUS_CHOICES = Object.keys(taskStatusLevels).map((status) => ({
  id: status,
  name: `moonstar.checklists.taskStatus.${status}`,
}));

function TaskStatusBadge({
  status,
}: {
  status: ChecklistReportEntry['taskStatus'];
}) {
  const translate = useTranslate();

  return (
    <StatusBadge level={taskStatusLevels[status]}>
      {translate(`moonstar.checklists.taskStatus.${status}`)}
    </StatusBadge>
  );
}

async function listExporter(entries: ChecklistReportEntry[]) {
  const mapped = entries.map((entry) => {
    const completion = entry.taskCompletionDetails?.completion;
    return {
      Checklist: entry.checklistSnapshotTitle,
      Unit: entry.unitSnapshotName,
      Task: entry.taskSnapshotTitle,
      Status: entry.taskStatus,
      'Start At': new Date(entry.checklistInstanceStartOn),
      'Completed At': entry.taskCompletedOn
        ? new Date(entry.taskCompletedOn)
        : undefined,
      'Completed By First Name': entry.taskCompletedByFirstName,
      'Completed By Last Name': entry.taskCompletedByLastName,
      Actions:
        completion && 'actions' in completion ? completion.actions : undefined,
      'Open Ended Response':
        completion?.type === 'OPEN_ENDED' ? completion.response : undefined,
      'Radio Select Response':
        completion?.type === 'RADIO_SELECT'
          ? completion.selectedOptions.join('\n')
          : undefined,
      'Photo Upload Response':
        completion?.type === 'PHOTO_UPLOAD'
          ? completion.imageUrls.join('\n')
          : undefined,
    };
  });

  await saveSpreadsheet({
    format: 'xlsx',
    data: mapped,
    basename: 'Checklist Report',
  });
}

interface ChecklistReportEntry {
  id: number;
  checklistId: number;
  checklistSnapshotTitle: string;
  unitId?: number;
  unitSnapshotName: string;
  taskId?: number;
  taskSnapshotTitle: string;
  taskStatus:
    | 'COMPLETED'
    | 'PENDING'
    | 'OVERDUE'
    | 'NOT_COMPLETED'
    | 'COMPLETED_LATE';
  checklistInstanceStartOn: string;
  taskCompletedOn?: string;
  taskCompletedByFirstName?: string;
  taskCompletedByLastName?: string;
  taskCompletionDetails?: {
    version: 1;
    completion: ChecklistTaskCompletion;
  };
}
